import React, { useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { supabase } from '../lib/supabase'

const teal = '#0D9488'
const plum = '#1E1040'

const TAG_COLORS = {
  schedule_c: { accent: teal, bg: 'rgba(13, 148, 136, 0.1)' },
  company_card: { accent: '#2563EB', bg: 'rgba(37, 99, 235, 0.1)' },
  reimbursable: { accent: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)' },
  personal: { accent: '#A855F7', bg: 'rgba(168, 85, 247, 0.1)' },
}

// Curated emoji grid for tag icons
const ICON_GRID = [
  '💼', '🏢', '💵', '🏠', '🏬', '📊',
  '🪙', '💳', '🧾', '📝', '📦', '🎯',
  '🏡', '🚗', '✈️', '🏨', '📱', '💻',
]

export default function ManageTags() {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [tags, setTags] = useState([])
  const [receiptCounts, setReceiptCounts] = useState({}) // { tag_id: count }
  const [editingId, setEditingId] = useState(null)
  const [editName, setEditName] = useState('')
  const [editIcon, setEditIcon] = useState('')
  const [editError, setEditError] = useState('')
  const [addingForType, setAddingForType] = useState(null) // the tag_type being added
  const [newName, setNewName] = useState('')
  const [newIcon, setNewIcon] = useState('💼')
  const [addError, setAddError] = useState('')
  const [deleteConfirmId, setDeleteConfirmId] = useState(null)
  const [deleteError, setDeleteError] = useState('')

  useEffect(() => { loadAll() }, [])

  const loadAll = async () => {
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { setLoading(false); return }

    const [{ data: tagsData }, { data: counts }] = await Promise.all([
      supabase.from('tags').select('*').eq('user_id', user.id).order('sort_order'),
      supabase.from('transactions').select('tag_id').eq('user_id', user.id),
    ])

    const countMap = {}
    ;(counts || []).forEach(t => {
      if (t.tag_id) countMap[t.tag_id] = (countMap[t.tag_id] || 0) + 1
    })
    setReceiptCounts(countMap)
    setTags(tagsData || [])
    setLoading(false)
  }

  const startEdit = (tag) => {
    setEditingId(tag.id)
    setEditName(tag.name)
    setEditIcon(tag.icon || '🏷️')
    setEditError('')
    setAddingForType(null)
    setDeleteConfirmId(null)
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditName('')
    setEditIcon('')
    setEditError('')
  }

  const saveEdit = async () => {
    const trimmed = editName.trim()
    if (!trimmed) { setEditError(t('manage_tags.error_name_required')); return }
    if (trimmed.length > 40) { setEditError(t('manage_tags.error_too_long')); return }

    const tag = tags.find(t => t.id === editingId)
    if (!tag) return

    const duplicate = tags.find(other =>
      other.id !== editingId
      && other.tag_type === tag.tag_type
      && other.name.toLowerCase() === trimmed.toLowerCase()
    )
    if (duplicate) {
      setEditError(t('manage_tags.error_duplicate', { type: t(`manage_tags.types.${tag.tag_type}.label`), name: trimmed }))
      return
    }

    const { data: updated, error: err } = await supabase
      .from('tags')
      .update({ name: trimmed, icon: editIcon, updated_at: new Date().toISOString() })
      .eq('id', editingId)
      .select()
      .single()

    if (err) { setEditError(err.message || t('manage_tags.error_save')); return }

    setTags(prev => prev.map(tt => tt.id === updated.id ? updated : tt))
    cancelEdit()
  }

  const startAdd = (tagType) => {
    setAddingForType(tagType)
    setNewName('')
    setNewIcon(tagType === 'schedule_c' ? '💼'
      : tagType === 'company_card' ? '🏢'
      : tagType === 'reimbursable' ? '💵'
      : '🏠')
    setAddError('')
    cancelEdit()
    setDeleteConfirmId(null)
  }

  const cancelAdd = () => {
    setAddingForType(null)
    setNewName('')
    setAddError('')
  }

  const saveAdd = async () => {
    const trimmed = newName.trim()
    if (!trimmed) { setAddError(t('manage_tags.error_name_required')); return }
    if (trimmed.length > 40) { setAddError(t('manage_tags.error_too_long')); return }

    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    const duplicate = tags.find(tt =>
      tt.tag_type === addingForType
      && tt.name.toLowerCase() === trimmed.toLowerCase()
    )
    if (duplicate) {
      setAddError(t('manage_tags.error_duplicate', { type: t(`manage_tags.types.${addingForType}.label`), name: trimmed }))
      return
    }

    // Next sort order within this type
    const lastInType = tags.filter(tt => tt.tag_type === addingForType)
      .reduce((max, tt) => Math.max(max, tt.sort_order || 0), 0)

    const { data: newTag, error: err } = await supabase
      .from('tags')
      .insert({
        user_id: user.id,
        name: trimmed,
        icon: newIcon,
        tag_type: addingForType,
        is_default: false,
        sort_order: lastInType + 1,
      })
      .select()
      .single()

    if (err) { setAddError(err.message || t('manage_tags.error_add')); return }

    setTags(prev => [...prev, newTag])
    cancelAdd()
  }

  const startDelete = (tag) => {
    setDeleteConfirmId(tag.id)
    setDeleteError('')
    setEditingId(null)
    setAddingForType(null)
  }

  const cancelDelete = () => {
    setDeleteConfirmId(null)
    setDeleteError('')
  }

  const confirmDelete = async () => {
    const tag = tags.find(tt => tt.id === deleteConfirmId)
    if (!tag) return

    // Block deletion of default tags
    if (tag.is_default) {
      setDeleteError(t('manage_tags.error_default_undeletable'))
      return
    }

    // Block if tag has receipts
    const count = receiptCounts[tag.id] || 0
    if (count > 0) {
      setDeleteError(t('manage_tags.error_in_use', { count }))
      return
    }

    const { error: err } = await supabase.from('tags').delete().eq('id', tag.id)
    if (err) { setDeleteError(err.message || t('manage_tags.error_delete')); return }

    setTags(prev => prev.filter(tt => tt.id !== tag.id))
    setDeleteConfirmId(null)
    setDeleteError('')
  }

  // Group tags by type for rendering
  const byType = {
    schedule_c: tags.filter(tt => tt.tag_type === 'schedule_c'),
    company_card: tags.filter(tt => tt.tag_type === 'company_card'),
    reimbursable: tags.filter(tt => tt.tag_type === 'reimbursable'),
    personal: tags.filter(tt => tt.tag_type === 'personal'),
  }

  if (loading) {
    return <div style={styles.loading}>{t('manage_tags.loading')}</div>
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>{t('manage_tags.title')}</h2>
        <p style={styles.subtitle}>
          {t('manage_tags.subtitle')}
        </p>
      </div>

      {Object.entries(byType).map(([tagType, tagList]) => {
        const colors = TAG_COLORS[tagType]
        return (
          <div key={tagType} style={styles.typeSection}>
            <div style={{ ...styles.typeHeader, borderLeftColor: colors.accent }}>
              <div>
                <div style={styles.typeName}>{t(`manage_tags.types.${tagType}.label`)}</div>
                <div style={styles.typeDesc}>{t(`manage_tags.types.${tagType}.description`)}</div>
              </div>
              <button
                style={{ ...styles.addTypeBtn, background: colors.accent }}
                onClick={() => startAdd(tagType)}
              >
                + {t('manage_tags.add_btn')}
              </button>
            </div>

            {/* Add form for this type */}
            {addingForType === tagType && (
              <div style={styles.addBox}>
                <div style={styles.editLabel}>{t('manage_tags.new_tag_label', { type: t(`manage_tags.types.${tagType}.label`) })}</div>
                <div style={styles.iconGrid}>
                  {ICON_GRID.map(emoji => (
                    <button
                      key={emoji}
                      type="button"
                      style={{
                        ...styles.iconBtn,
                        ...(newIcon === emoji ? { ...styles.iconBtnActive, borderColor: colors.accent } : {}),
                      }}
                      onClick={() => setNewIcon(emoji)}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  style={styles.input}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); saveAdd() }
                    if (e.key === 'Escape') { e.preventDefault(); cancelAdd() }
                  }}
                  placeholder={t(`manage_tags.types.${tagType}.placeholder`)}
                  maxLength={40}
                  autoFocus
                />
                {addError && <div style={styles.error}>{addError}</div>}
                <div style={styles.editActions}>
                  <button style={styles.btnSecondary} onClick={cancelAdd}>{t('common.cancel')}</button>
                  <button
                    style={{ ...styles.btnPrimary, background: colors.accent }}
                    onClick={saveAdd}
                  >
                    {t('manage_tags.add_cta', { icon: newIcon, name: newName.trim() || t('manage_tags.default_tag_name') })}
                  </button>
                </div>
              </div>
            )}

            {/* Tags in this type */}
            <div style={styles.tagList}>
              {tagList.map(tag => (
                <div key={tag.id} style={styles.tagRow}>
                  {editingId === tag.id ? (
                    <div style={styles.editBlock}>
                      <div style={styles.editLabel}>{t('manage_tags.edit_label')}</div>
                      <div style={styles.iconGrid}>
                        {ICON_GRID.map(emoji => (
                          <button
                            key={emoji}
                            type="button"
                            style={{
                              ...styles.iconBtn,
                              ...(editIcon === emoji ? { ...styles.iconBtnActive, borderColor: colors.accent } : {}),
                            }}
                            onClick={() => setEditIcon(emoji)}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                      <input
                        type="text"
                        style={styles.input}
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); saveEdit() }
                          if (e.key === 'Escape') { e.preventDefault(); cancelEdit() }
                        }}
                        maxLength={40}
                        autoFocus
                      />
                      {editError && <div style={styles.error}>{editError}</div>}
                      <div style={styles.editActions}>
                        <button style={styles.btnSecondary} onClick={cancelEdit}>{t('common.cancel')}</button>
                        <button style={{ ...styles.btnPrimary, background: colors.accent }} onClick={saveEdit}>
                          {t('manage_tags.save_changes')}
                        </button>
                      </div>
                    </div>
                  ) : deleteConfirmId === tag.id ? (
                    <div style={styles.deleteBlock}>
                      <div style={styles.deleteText}>
                        <Trans
                          i18nKey="manage_tags.delete_confirm"
                          values={{ icon: tag.icon, name: tag.name }}
                          components={{ strong: <strong /> }}
                        />
                        {tag.is_default && ' ' + t('manage_tags.delete_default_note')}
                      </div>
                      {deleteError && <div style={styles.error}>{deleteError}</div>}
                      <div style={styles.editActions}>
                        <button style={styles.btnSecondary} onClick={cancelDelete}>{t('common.cancel')}</button>
                        <button
                          style={styles.btnDanger}
                          onClick={confirmDelete}
                          disabled={tag.is_default}
                        >
                          {t('manage_tags.confirm_delete')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div style={styles.rowMain}>
                        <span style={styles.rowIcon}>{tag.icon}</span>
                        <div>
                          <div style={styles.rowName}>{tag.name}</div>
                          <div style={styles.rowMeta}>
                            {t('manage_tags.receipt_count', { count: receiptCounts[tag.id] || 0 })}
                            {tag.is_default && <span style={styles.defaultBadge}>{t('manage_tags.default_badge')}</span>}
                          </div>
                        </div>
                      </div>
                      <div style={styles.rowActions}>
                        <button style={styles.actionBtn} onClick={() => startEdit(tag)}>{t('common.edit')}</button>
                        {!tag.is_default && (
                          <button style={styles.actionBtnDanger} onClick={() => startDelete(tag)}>{t('common.delete')}</button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}
              {tagList.length === 0 && (
                <div style={styles.emptyRow}>
                  {t('manage_tags.empty_type')}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const styles = {
  container: { fontFamily: "'Plus Jakarta Sans', sans-serif", color: plum },
  loading: { padding: '32px', textAlign: 'center', color: '#9CA3AF', fontSize: '14px' },
  header: { marginBottom: '24px' },
  title: {
    fontFamily: "'Fraunces', serif", fontSize: '22px', fontWeight: 800,
    color: plum, letterSpacing: '-0.3px', margin: 0, marginBottom: '4px',
  },
  subtitle: {
    fontSize: '13px', color: '#6B7280', fontWeight: 500,
    margin: 0, lineHeight: 1.5,
  },
  typeSection: { marginBottom: '28px' },
  typeHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', background: '#F9FAFB',
    borderLeft: '4px solid', borderRadius: '4px',
    marginBottom: '10px', gap: '12px',
  },
  typeName: {
    fontFamily: "'Fraunces', serif", fontSize: '16px',
    fontWeight: 800, color: plum, letterSpacing: '-0.2px',
  },
  typeDesc: { fontSize: '12px', color: '#6B7280', marginTop: '2px', fontWeight: 500 },
  addTypeBtn: {
    padding: '7px 14px', color: '#fff', border: 'none',
    borderRadius: '100px', fontSize: '12px', fontWeight: 700,
    cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
    flexShrink: 0,
  },
  tagList: {
    background: '#fff', border: '1px solid #E5E7EB',
    borderRadius: '12px', overflow: 'hidden',
  },
  tagRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 18px', borderTop: '1px solid #F3F4F6', gap: '12px',
    flexWrap: 'wrap',
  },
  rowMain: { display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 },
  rowIcon: { fontSize: '22px' },
  rowName: { fontSize: '14px', fontWeight: 700, color: plum },
  rowMeta: {
    fontSize: '11px', color: '#9CA3AF', marginTop: '2px', fontWeight: 500,
    display: 'flex', alignItems: 'center', gap: '8px',
  },
  defaultBadge: {
    fontSize: '9px', fontWeight: 700, color: '#6B7280',
    background: '#F3F4F6', padding: '2px 6px', borderRadius: '3px',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },
  rowActions: { display: 'flex', gap: '8px' },
  actionBtn: {
    padding: '6px 14px', background: '#fff', color: plum,
    border: '1.5px solid #E5E7EB', borderRadius: '100px',
    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  actionBtnDanger: {
    padding: '6px 14px', background: '#fff', color: '#DC2626',
    border: '1.5px solid #FECACA', borderRadius: '100px',
    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  emptyRow: {
    padding: '16px 18px', color: '#9CA3AF', fontSize: '13px',
    textAlign: 'center', fontStyle: 'italic',
  },
  editBlock: { flex: 1, background: '#F9FAFB', padding: '12px', borderRadius: '10px' },
  editLabel: {
    fontSize: '11px', fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px',
  },
  iconGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)',
    gap: '6px', marginBottom: '12px',
  },
  iconBtn: {
    aspectRatio: '1/1', background: '#fff',
    border: '1.5px solid #E5E7EB', borderRadius: '8px',
    fontSize: '20px', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 0,
  },
  iconBtnActive: { background: '#fff', borderWidth: '2.5px', transform: 'scale(1.05)' },
  input: {
    width: '100%', padding: '10px 12px', borderRadius: '8px',
    border: '1.5px solid #E5E7EB', fontSize: '14px',
    outline: 'none', boxSizing: 'border-box', color: plum,
    fontFamily: "'Plus Jakarta Sans', sans-serif", background: '#fff',
  },
  error: {
    background: '#FEE2E2', color: '#991B1B',
    padding: '8px 12px', borderRadius: '8px',
    fontSize: '12px', fontWeight: 500, marginTop: '10px',
  },
  editActions: { display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' },
  btnPrimary: {
    padding: '9px 18px', color: '#fff', border: 'none',
    borderRadius: '100px', fontSize: '12px', fontWeight: 700,
    cursor: 'pointer', fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnSecondary: {
    padding: '9px 18px', background: '#fff', color: plum,
    border: '1.5px solid #E5E7EB', borderRadius: '100px',
    fontSize: '12px', fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  btnDanger: {
    padding: '9px 18px', background: '#DC2626', color: '#fff',
    border: 'none', borderRadius: '100px',
    fontSize: '12px', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Plus Jakarta Sans', sans-serif",
  },
  addBox: {
    background: '#F9FAFB', border: '2px solid #E5E7EB',
    borderRadius: '12px', padding: '14px', marginBottom: '10px',
  },
  deleteBlock: { flex: 1, background: '#FEF3C7', padding: '12px', borderRadius: '10px' },
  deleteText: { fontSize: '13px', color: '#92400E', fontWeight: 500, lineHeight: 1.5 },
}
